import React, { useCallback, useId, useMemo } from 'react';
import styles from './rotation-content.module.scss';
import { notification } from 'antd';
import { formatFullName } from 'shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { Button, IconButton } from 'src/components/design-system';
import { useUpdateRotation } from 'shared/hooks/rotations.hooks';
import { useRotationsContext } from '../rotations-context';
import { useSelector } from 'react-redux';
import { Select } from 'src/components/design-system/forms';
import { Owners } from 'src/components/common/owners';

const RotationOwners = () => {
  const { selectedRotation: rotation, lang, canEdit, analyticsTriggers } = useRotationsContext();
  const updateRotation = useUpdateRotation();
  const owners = useSelector(state => state.owners.items);
  const [editing, setEditing] = React.useState(false);
  const [managedOwners, setManagedOwners] = React.useState(rotation?.ownerIds);
  const [api, contextHolder] = notification.useNotification();
  const ownersSelectId = useId();

  const options = useMemo(
    () =>
      Object.values(owners)
        .map(owner => ({
          label: formatFullName(owner),
          value: owner.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [owners],
  );

  const showToast = useCallback(
    message => {
      api.error({
        title: message || lang.ERROR_OCCURRED,
        placement: 'bottomRight',
      });
    },
    [api, lang.ERROR_OCCURRED],
  );

  const handleOwnerChange = useCallback(value => {
    setManagedOwners(value || []);
  }, []);

  const handleCancel = useCallback(() => {
    setEditing(false);
    setManagedOwners(rotation?.ownerIds);
  }, [rotation]);

  const handleSubmit = async () => {
    setEditing(false);
    try {
      await updateRotation.mutateAsync({
        rotationId: rotation?.id,
        payload: { ownerIds: managedOwners },
      });
      analyticsTriggers?.onRotationDetailsUpdated({ id: rotation.id, ownerIds: managedOwners });
    } catch (e) {
      showToast(e.response?.data?.message);
    }
  };

  return editing ? (
    <div className={styles.editOwners}>
      {contextHolder}
      <label htmlFor={ownersSelectId} className={styles.editOwnersTitle}>
        {lang.ROTATION_DIRECTOR}
      </label>
      <Select
        id={ownersSelectId}
        mode="multiple"
        size="large"
        onChange={(f, v) => handleOwnerChange(v)}
        options={options}
        value={managedOwners}
        style={{ maxWidth: '70%' }}
      />
      <div className={styles.actionButtons}>
        <Button type="primary" onClick={handleSubmit}>
          {lang.SUBMIT}
        </Button>
        <Button onClick={handleCancel}>{lang.CANCEL}</Button>
      </div>
    </div>
  ) : (
    <div className={styles.owners}>
      {lang.ROTATION_DIRECTOR}
      {': '}
      {rotation?.ownerIds?.length > 0 ? (
        <Owners ownerIds={rotation.ownerIds} />
      ) : (
        <span className={styles.noOwner}>{lang.UNASSIGNED}</span>
      )}
      {canEdit && (
        <IconButton
          label={lang.EDIT_OWNERS}
          onClick={() => setEditing(true)}
          className={styles.ownersIconButton}>
          <FontAwesomeIcon icon={faPencil} className={styles.ownersIcon} />
        </IconButton>
      )}
    </div>
  );
};

export default RotationOwners;
